// Import icons using absolute paths.
import mtnIcon from "@/pages/wallet/assets/mtn-pay.webp";
import vodafoneIcon from "@/pages/wallet/assets/vodagone.webp";
import airtelIcon from "@/pages/wallet/assets/airtel.webp";
import waveIcon from "@/pages/wallet/assets/wave-pay.webp";
import moovIcon from "@/pages/wallet/assets/moov-pay.webp";
import orangeIcon from "@/pages/wallet/assets/orange-pay.webp";
import mtnPayIcon from "@/pages/wallet/assets/mtn-pay.webp";
import tmoneyIcon from "@/pages/wallet/assets/T-money.webp";
import { getCurrentCountryCode } from "@/utils/countryConfigUtils";
import { IFpayParams } from "@/api/apiRsp/IFWalletRsp";
import etisalatIcon from "@/pages/wallet/assets/Etisalat.webp";
import wecashIcon from "@/pages/wallet/assets/wecash.webp";
import telecelIcon from "@/pages/wallet/assets/telecel.webp";
import palmIcon from "@/pages/wallet/assets/palm.webp";
import zamtelIcon from "@/pages/wallet/assets/zamtel.webp";
import mpesaIcon from "@/pages/wallet/assets/mpesa.webp";
import speiIcon from "@/pages/wallet/assets/spei.webp";
import paypalIcon from "@/pages/wallet/assets/PayPal.webp";
import miOxxoIcon from "@/pages/wallet/assets/Mi OXXO.webp";
import mercadoPagoIcon from "@/pages/wallet/assets/Mercado Pago.webp";
import codiIcon from "@/pages/wallet/assets/CoDi.webp";
import bbvaIcon from "@/pages/wallet/assets/BBVA.webp";
import applePayIcon from "@/pages/wallet/assets/Apple Pay.webp";
import tzAirtelIcon from "@/pages/wallet/assets/tz-airtel.png";
import tzTigoIcon from "@/pages/wallet/assets/tz-tigo.png";
import tzVodacomIcon from "@/pages/wallet/assets/tz-vodacom.png";
import tzHalopesaIcon from "@/pages/wallet/assets/tz-halopesa.png";
import tzTpesaIcon from "@/pages/wallet/assets/tz-tpesa.png";

// Payment method icon map.
export const PAYMENT_ICONS = {
  MTN: mtnIcon,
  Vodafone: vodafoneIcon,
  AirtelTigo: airtelIcon,
  Wave: waveIcon,
  Momo: mtnPayIcon,
  Orange: orangeIcon,
  Moov: moovIcon,
  Tmoney: tmoneyIcon,
  Mpesa: mpesaIcon,
} as const;

/**
 * Return an icon path based on the payment method name.
 * @param payName Payment method name.
 * @returns Icon path or null.
 */
export const getPaymentIcon = (payName: string | any): string | null => {
  if (!payName || typeof payName !== "string") return null;

  // Normalize for matching (remove separators and lowercase).
  const normalizedName = payName.toLowerCase().replace(/[\s\-_]/g, "");
  const countryCode = getCurrentCountryCode()?.toUpperCase();

  if (countryCode === "EG") {
    if (normalizedName.includes("etisalat")) {
      return etisalatIcon;
    }
    if (normalizedName.includes("wecash")) {
      return wecashIcon;
    }
  }

  if (countryCode === "GH" && normalizedName.includes("telecel")) {
    return telecelIcon;
  }

  if (countryCode === "NG") {
    if (normalizedName.includes("zamtel")) {
      return zamtelIcon;
    }
    if (normalizedName.includes("palm")) {
      return palmIcon;
    }
  }

  if (countryCode === "ZM" && normalizedName.includes("zamtel")) {
    return zamtelIcon;
  }

  if (countryCode === "KE" && normalizedName.includes("mpesa")) {
    return mpesaIcon;
  }

  if (countryCode === "TZ") {
    if (normalizedName.includes("mpesa")) {
      return mpesaIcon;
    }
    if (normalizedName.includes("airtel")) {
      return tzAirtelIcon;
    }
    if (normalizedName.includes("tigo")) {
      return tzTigoIcon;
    }
    if (normalizedName.includes("vodacom")) {
      return tzVodacomIcon;
    }
    if (normalizedName.includes("halopesa")) {
      return tzHalopesaIcon;
    }
    if (normalizedName.includes("tpesa")) {
      return tzTpesaIcon;
    }
  }

  if (countryCode === "MX" && normalizedName.includes("spei")) {
    return speiIcon;
  }
  if (countryCode === "MX") {
    if (normalizedName.includes("paypal")) {
      return paypalIcon;
    }
    if (normalizedName.includes("oxxo")) {
      return miOxxoIcon;
    }
    if (normalizedName.includes("mercadopago")) {
      return mercadoPagoIcon;
    }
    if (normalizedName.includes("codi")) {
      return codiIcon;
    }
    if (normalizedName.includes("bbva")) {
      return bbvaIcon;
    }
    if (normalizedName.includes("applepay")) {
      return applePayIcon;
    }
  }

  if (normalizedName.includes("mtn")) {
    return PAYMENT_ICONS.MTN;
  } else if (normalizedName.includes("vodafone")) {
    return PAYMENT_ICONS.Vodafone;
  } else if (normalizedName.includes("airtel")) {
    return PAYMENT_ICONS.AirtelTigo;
  } else if (normalizedName.includes("wave")) {
    return PAYMENT_ICONS.Wave;
  } else if (normalizedName.includes("momo")) {
    return PAYMENT_ICONS.Momo;
  } else if (normalizedName.includes("orange")) {
    return PAYMENT_ICONS.Orange;
  } else if (normalizedName.includes("moov")) {
    return PAYMENT_ICONS.Moov;
  } else if (normalizedName.includes("tmoney")) {
    return PAYMENT_ICONS.Tmoney;
  }

  return null;
};

const PAYMENT_METHOD_ORDER: Record<string, string[]> = {
  default: [
    "airteltigo",
    "airtel",
    "mtn",
    "vodafone",
    "telecel",
    "orange",
    "moov",
    "wave",
    "momo",
    "tmoney",
    "mpesa",
    "etisalat",
    "wecash",
    "zamtel",
    "palm",
    "spei",
    "pix",
    "usdt",
    "bank",
  ],
  sn: ["wave", "orange"],
  tg: ["tmoney", "moov"],
  bj: ["momo", "moov"],
  bf: ["orange", "moov"],
  ci: ["wave", "momo", "orange", "moov"],
  gh: ["mtn", "vodafone", "airteltigo", "airtel", "telecel"],
  ng: ["palm", "zamtel", "mtn", "airtel"],
  zm: ["mtn", "airtel", "zamtel"],
  eg: ["vodafone", "orange", "etisalat", "wecash"],
  ke: ["mpesa", "airtel"],
  tz: ["airtel", "tigo", "vodacom", "halopesa", "mpesa", "tpesa"],
  // Mexico-only payment methods (other countries don't show these icons yet).
  mx: ["spei", "paypal", "oxxo", "mercadopago", "codi", "bbva", "applepay"],
};

const normalizePaymentName = (value: string): string =>
  value.toLowerCase().replace(/[\s_-]/g, "");

const getPaymentOrder = (countryCode?: string): string[] => {
  const code = (countryCode || getCurrentCountryCode() || "").toLowerCase();
  return PAYMENT_METHOD_ORDER[code] || PAYMENT_METHOD_ORDER.default;
};

// Used by non-wallet pages (e.g. home footer) to show a per-country
// best-effort set of available payment method icons without calling APIs.
export const getPaymentOrderKeys = (countryCode?: string): string[] => {
  return getPaymentOrder(countryCode);
};

const getPaymentRank = (name: string, order: string[]): number => {
  const normalizedName = normalizePaymentName(name);
  for (let i = 0; i < order.length; i += 1) {
    if (normalizedName.includes(normalizePaymentName(order[i]))) {
      return i;
    }
  }
  return order.length;
};

export const sortPaymentMethods = (
  methods: string[],
  countryCode?: string,
): string[] => {
  if (!methods?.length) return methods || [];
  const order = getPaymentOrder(countryCode);
  return methods
    .map((method, index) => ({
      method,
      index,
      rank: getPaymentRank(method, order),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((item) => item.method);
};

export const sortPaymentParams = (
  payParams: IFpayParams[],
  countryCode?: string,
): IFpayParams[] => {
  if (!payParams?.length) return payParams || [];
  const order = getPaymentOrder(countryCode);
  return payParams
    .map((item, index) => ({
      item,
      index,
      rank: getPaymentRank(item.payName || "", order),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.item);
};

/**
 * Check whether a payment name matches a given operator.
 * @param payName Payment method name.
 * @param operator Operator name.
 * @returns True if it matches.
 */
export const isPaymentMethodMatch = (
  payName: string,
  operator: string,
): boolean => {
  if (!payName || !operator) return false;

  return payName.toLowerCase() === operator.toLowerCase();
};
